import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../assets/css/style.css";
import { FaEyeSlash, FaEye } from "react-icons/fa";
import Logo from "../../assets/Images/logo/logo.svg";
import Lexik from "../../assets/Images/logo/logoName.svg";
import logobg from "../../assets/Images/logobg.png";
import VerticalSpace from "../../Components/VerticalSpace";
import Toast from "../../Components/Common/Toast";
import { baseURL, postRequest } from "../../api/apiClient";

const ForgotPassword: React.FC = () => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [email, setEmail] = useState("");
  const [otpPin, setOtpPin] = useState("");
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const otpInputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

  // Handle OTP input change
  const handleOtpChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }

    // Update otpPin state for verification
    setOtpPin(newOtp.join(""));
  };

  // Handle backspace/delete
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
    
    if (e.key === "Enter") {
      handleVerifyOTP();
    }
  };

  // Handle paste
  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newOtp = [...otp];
    
    pastedData.split("").forEach((char, index) => {
      if (index < 6) {
        newOtp[index] = char;
      }
    });
    
    setOtp(newOtp);
    setOtpPin(newOtp.join(""));
    
    // Focus last filled input or last input
    const lastFilledIndex = Math.min(pastedData.length, 5);
    otpInputRefs.current[lastFilledIndex]?.focus();
  };

  // Step 1: Send OTP
  const handleSendOTP = async () => {
    if (!email) {
      Toast("Email is required", "error");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      Toast("Invalid email address", "error");
      return;
    }

    try {
      setIsLoading(true);
      const response = await postRequest(`${baseURL}/forgot-password`, {
        email,
      });
      if (response?.status === 200) {
        Toast(response?.data?.statusMessage || response?.data?.message || "OTP sent successfully", "success");
        setStep(2);
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async () => {
    const otpValue = otp.join("");
    
    if (!otpValue) {
      Toast("OTP is required", "error");
      return;
    }

    if (otpValue.length !== 6) {
      Toast("OTP must be 6 digits", "error");
      return;
    }

    try {
      setIsLoading(true);
      const response = await postRequest(`${baseURL}/verify-otp`, {
        email,
        otpPin: otpValue,
      });

      if (response?.status === 200) {
        Toast(response?.data?.statusMessage || response?.data?.message || "OTP verified successfully", "success");
        setStep(3);
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      Toast("All fields are required", "error");
      return;
    }

    if (newPassword.length < 8) {
      Toast("Password must be at least 8 characters long", "error");
      return;
    }

    if (newPassword !== confirmPassword) {
      Toast("Passwords do not match", "error");
      return;
    }

    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
      Toast(
        "Password must contain uppercase, lowercase, number and special character",
        "error"
      );
      return;
    }

    try {
      setIsLoading(true);
      const response = await postRequest(`${baseURL}/reset-password`, {
        email,
        newPassword,
      });

      if (response?.status === 200) {
        Toast(response?.data?.statusMessage || response?.data?.message || "Password reset successful", "success");
        setTimeout(() => {
          navigate("/login");
        }, 1500);
      }
    } catch (error) {
      console.error("Error resetting password:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      setIsLoading(true);
      const response = await postRequest(`${baseURL}/forgot-password`, {
        email,
      });

      if (response?.status === 200) {
        Toast(response?.data?.statusMessage || response?.data?.message || "OTP resent successfully", "success");
      } else {
        Toast("Failed to resend OTP", "error");
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      if (step === 1) handleSendOTP();
      else if (step === 2) handleVerifyOTP();
      else if (step === 3) handleResetPassword();
    }
  };

  const handleBack = () => {
    if (step === 1) {
      navigate("/login");
    } else if (step === 2) {
      setStep(1);
      setOtp(["", "", "", "", "", ""]);
      setOtpPin("");
    } else if (step === 3) {
      setStep(2);
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  return (
    <div
      className="fullscreen-background"
      style={{
        backgroundImage: `url(${logobg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div
        className="row justify-content-around align-items-center fullscreen-content"
        style={{ margin: 0 }}
      >
        <div className="col-md-6">
          <div
            className="card-body login-image text-center"
            style={{ padding: "0px 0px 0px 70px" }}
          >
            <div className="col">
              <img
                src={Logo}
                alt="Logo"
                className="logo"
                style={{ verticalAlign: "baseline" }}
              />
              {"  "}
              <img
                src={Lexik}
                alt="Lexik"
                className="lexik_name"
                style={{ verticalAlign: "baseline", marginLeft: "10px" }}
              />
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div
            className="card-body input-box"
            style={{ padding: "0px 130px 0px 100px" }}
          >
            {/* Step 1: Enter Email */}
            {step === 1 && (
              <>
                <h2 style={{ color: "#fff", marginBottom: "20px" }}>
                  Forgot Password
                </h2>
                <p style={{ color: "#fff", marginBottom: "30px" }}>
                  Enter your email address and we'll send you an OTP to reset
                  your password.
                </p>
                <div className="form-group">
                  <label htmlFor="email" style={{ color: "#fff" }}>
                    Email
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter your email"
                  />
                </div>

                <VerticalSpace height={40} />
                <div className="button" style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", gap: "15px", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{
                      backgroundColor: "transparent",
                      borderRadius: "10px",
                      padding: "10px 30px",
                      border: "1px solid #fff",
                      color: "#fff",
                      fontSize: '14px',
                      minWidth: '200px',
                      flex: "1"
                    }}
                    onClick={handleBack}
                  >
                    Back to Login
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{
                      backgroundColor: "#7542E4",
                      borderRadius: "10px",
                      padding: "10px 30px",
                      fontSize: '14px',
                      minWidth: '200px',
                      flex: "1"
                    }}
                    onClick={handleSendOTP}
                  >
                    {isLoading ? (
                      <div className="d-flex align-items-center justify-content-center">
                        <div className="spinner-border" role="status">
                          <span className="sr-only"></span>
                        </div>
                      </div>
                    ) : (
                      "Send OTP"
                    )}
                  </button>
                </div>
              </>
            )}

            {/* Step 2: Verify OTP */}
            {step === 2 && (
              <>
                <h2 style={{ color: "#fff", marginBottom: "20px" }}>
                  Verify OTP
                </h2>
                <p style={{ color: "#fff", marginBottom: "30px" }}>
                  Enter the 6-digit OTP sent to {email}
                </p>
                <div className="form-group">
                  <label htmlFor="otp" style={{ color: "#fff", marginBottom: "15px" }}>
                    OTP
                  </label>
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      justifyContent: "center",
                      marginBottom: "20px",
                    }}
                  >
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (otpInputRefs.current[index] = el)}
                        type="text"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        onPaste={handleOtpPaste}
                        style={{
                          width: "50px",
                          height: "50px",
                          textAlign: "center",
                          fontSize: "20px",
                          fontWeight: 600,
                          border: "2px solid #fff",
                          borderRadius: "8px",
                          backgroundColor: "transparent",
                          color: "#fff",
                          outline: "none",
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = "#7542E4";
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = "#fff";
                        }}
                      />
                    ))}
                  </div>
                </div>

                <VerticalSpace height={20} />
                <div style={{ textAlign: "right" }}>
                  <button
                    type="button"
                    style={{
                      background: "none",
                      border: "none",
                      color: "#7542E4",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    onClick={handleResendOTP}
                    disabled={isLoading}
                  >
                    Resend OTP
                  </button>
                </div>

                <VerticalSpace height={40} />
                <div className="button" style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", gap: "15px", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{
                      backgroundColor: "transparent",
                      borderRadius: "10px",
                      padding: "10px 30px",
                      border: "1px solid #fff",
                      color: "#fff",
                      fontSize: '14px',
                      minWidth: '200px',
                      flex: "1"
                    }}
                    onClick={handleBack}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{
                      backgroundColor: "#7542E4",
                      borderRadius: "10px",
                      padding: "10px 30px",
                      fontSize: '14px',
                      minWidth: '200px',
                      flex: "1"
                    }}
                    onClick={handleVerifyOTP}
                  >
                    {isLoading ? (
                      <div className="d-flex align-items-center justify-content-center">
                        <div className="spinner-border" role="status">
                          <span className="sr-only"></span>
                        </div>
                      </div>
                    ) : (
                      "Verify OTP"
                    )}
                  </button>
                </div>
              </>
            )}

            {/* Step 3: Reset Password */}
            {step === 3 && (
              <>
                <h2 style={{ color: "#fff", marginBottom: "20px" }}>
                  Reset Password
                </h2>
                <p style={{ color: "#fff", marginBottom: "30px" }}>
                  Enter your new password
                </p>
                <div className="form-group">
                  <label htmlFor="newPassword" style={{ color: "#fff" }}>
                    New Password
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      className="form-control"
                      id="newPassword"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                      }}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </span>
                  </div>
                </div>

                <VerticalSpace height={24} />
                <div className="form-group">
                  <label htmlFor="confirmPassword" style={{ color: "#fff" }}>
                    Confirm Password
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      className="form-control"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Confirm new password"
                    />
                    <span
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                      }}
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </span>
                  </div>
                </div>

                <VerticalSpace height={40} />
                <div className="button" style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", gap: "15px", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{
                      backgroundColor: "transparent",
                      borderRadius: "10px",
                      padding: "10px 30px",
                      border: "1px solid #fff",
                      color: "#fff",
                      fontSize: '14px',
                      minWidth: '200px',
                      flex: "1"
                    }}
                    onClick={handleBack}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{
                      backgroundColor: "#7542E4",
                      borderRadius: "10px",
                      padding: "10px 30px",
                      fontSize: '14px',
                      minWidth: '200px',
                      flex: "1"
                    }}
                    onClick={handleResetPassword}
                  >
                    {isLoading ? (
                      <div className="d-flex align-items-center justify-content-center">
                        <div className="spinner-border" role="status">
                          <span className="sr-only"></span>
                        </div>
                      </div>
                    ) : (
                      "Reset Password"
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
